import { FC, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { CreatePayer, PayerResponse } from "@/domain/payer.interface";

interface PayerFormProps {
	onSubmit: (data: CreatePayer) => void;
	onCancel: () => void;
	initialData?: PayerResponse | null;
	isLoading?: boolean;
}

const PayerForm: FC<PayerFormProps> = ({
	onSubmit,
	onCancel,
	initialData,
	isLoading = false,
}) => {
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CreatePayer>({
		defaultValues: {
			name: "",
			description: "",
			regularExpression: "",
			autogenerated: "N",
			status: "ACTIVE",
		},
	});

	useEffect(() => {
		if (initialData) {
			reset({
				name: initialData.name,
				description: initialData.description,
				regularExpression: initialData.regularExpression,
				autogenerated: initialData.autogenerated,
				status: initialData.status,
			});
		}
	}, [initialData, reset]);

	const handleFormSubmit = (data: CreatePayer) => {
		onSubmit(data);
	};

	return (
		<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Controller
						name="name"
						control={control}
						rules={{ required: "Name is required" }}
						render={({ field }) => (
							<TextField
								{...field}
								label="Name"
								fullWidth
								error={!!errors.name}
								helperText={errors.name?.message}
								disabled={isLoading}
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12}>
					<Controller
						name="description"
						control={control}
						rules={{ required: "Description is required" }}
						render={({ field }) => (
							<TextField
								{...field}
								label="Description"
								fullWidth
								multiline
								rows={3}
								error={!!errors.description}
								helperText={errors.description?.message}
								disabled={isLoading}
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12}>
					<Controller
						name="regularExpression"
						control={control}
						rules={{ required: "Regular Expression is required" }}
						render={({ field }) => (
							<TextField
								{...field}
								label="Regular Expression"
								fullWidth
								error={!!errors.regularExpression}
								helperText={errors.regularExpression?.message}
								disabled={isLoading}
							/>
						)}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<FormControl fullWidth error={!!errors.autogenerated}>
						<InputLabel>Autogenerated</InputLabel>
						<Controller
							name="autogenerated"
							control={control}
							rules={{ required: "Autogenerated is required" }}
							render={({ field }) => (
								<Select {...field} label="Autogenerated" disabled={isLoading}>
									<MenuItem value="Y">Yes</MenuItem>
									<MenuItem value="N">No</MenuItem>
								</Select>
							)}
						/>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={6}>
					<FormControl fullWidth error={!!errors.status}>
						<InputLabel>Status</InputLabel>
						<Controller
							name="status"
							control={control}
							rules={{ required: "Status is required" }}
							render={({ field }) => (
								<Select {...field} label="Status" disabled={isLoading}>
									<MenuItem value="ACTIVE">Active</MenuItem>
									<MenuItem value="INACTIVE">Inactive</MenuItem>
								</Select>
							)}
						/>
					</FormControl>
				</Grid>

				<Grid item xs={12}>
					<Box display="flex" justifyContent="flex-end" gap={2}>
						<Button onClick={onCancel} disabled={isLoading}>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							disabled={isLoading}
						>
							{initialData ? "Update" : "Create"}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};

export default PayerForm;
